/**
 * BestPicksRow.tsx
 *
 * Compact horizontal scroll row for curated venue sections.
 * Each card is a slim tap target — name + status + one deal pill.
 * Tapping navigates directly to the venue detail page.
 */

import React, { useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import type { BestPicksSection } from '../utils/bestPicks'
import type { UseFavoritesReturn } from '../hooks/useFavorites'
import type { UserLocation, Venue, DealItem } from '../types'
import { isVenueActiveNow, getEffectiveVenueStatus, distanceMiles, fmtDistance, getTodayDOW } from '../utils/filters'
import { STATUS_VISUALS } from '../utils/happeningNow'
import { DEAL_TYPE_COLORS, DEAL_TYPE_LABELS } from '../types'
import { Analytics } from '../services/analytics'
import { fmtDealValue } from '../utils/dealFormat'

// Does this deal have a directly comparable dollar price? A "$2 off"
// deal is not a $2 item — treating it as one in a MIN-price sort would
// rank a discounted $14 cocktail above a real $4 beer. Same principle
// as VenueCard's hasComparablePrice.
function hasComparablePrice(d: any): boolean {
  const vt = d.value_type === 'amount_off' || d.value_type === 'percent_off' ? d.value_type : 'price'
  return vt === 'price' && (d.price ?? d.value) != null
}

const SECTION_DEAL_TYPE: Record<string, string> = {
  cocktail_deals:  'cocktail',
  food_deals:      'food',
  cheapest_drinks: 'beer',
  happening_now:   '',
  starting_soon:   '',
  trending_tonight:'',
  rooftop_picks:   '',
  patio_picks:     '',
  waterfront_picks:'',
  date_night:      'cocktail',
}

const DEAL_ORDER = ['beer', 'cocktail', 'liquor', 'wine', 'food', 'general']

function getBestDealForSection(venue: Venue, sectionId: string): DealItem | null {
  const preferredType = SECTION_DEAL_TYPE[sectionId]
  // Only schedules that actually apply TODAY — a venue's Monday-only $5
  // Hurricanes was winning on category+price and showing up on a
  // Wednesday card, because nothing here ever checked which day a
  // schedule was for. Day match only (not live-right-now): Trending
  // Tonight legitimately shows a not-yet-open venue's later-today deal
  // (e.g. "Opens at 2 PM"), so restricting to isScheduleActiveNow would
  // wrongly hide those instead of just the wrong-day ones.
  const todayAbbr = getTodayDOW()
  const allDeals = (venue.schedules ?? [])
    .filter(s => s.days.includes(todayAbbr))
    .flatMap(s => s.deals)
  if (!allDeals.length) return null

  // Only deals with a real comparable PRICE participate in the
  // cheapest-first sort. Discount deals (no direct price) sort after
  // priced ones within the same type — previously `(a.price ?? 999)`
  // meant a discount silently sorted as if it cost $999, effectively
  // always last regardless of type match, and the "cheapest" pick could
  // still be a discount deal with nothing to actually compare it by.
  const byPrice = (a: DealItem, b: DealItem) => {
    const aHas = hasComparablePrice(a as any)
    const bHas = hasComparablePrice(b as any)
    if (aHas && !bHas) return -1
    if (!aHas && bHas) return 1
    if (aHas && bHas) return (a.price ?? 0) - (b.price ?? 0)
    return 0
  }

  if (preferredType) {
    const matching = allDeals.filter(d => d.type === preferredType)
    if (matching.length > 0) {
      return [...matching].sort(byPrice)[0]
    }
  }

  // Fallback for sections with no preferred deal type (Happening Now,
  // Trending Tonight, category picks like Rooftop). Category order comes
  // first (beer usually reads as the most universal headline), but WITHIN
  // the same category, price breaks the tie — previously ties fell back
  // to raw array order, meaning a $20 bucket could out-rank a genuine $1
  // discount at the same venue for no reason other than which one
  // happened to be entered first.
  return [...allDeals].sort((a, b) => {
    const orderDiff = DEAL_ORDER.indexOf(a.type) - DEAL_ORDER.indexOf(b.type)
    if (orderDiff !== 0) return orderDiff
    return byPrice(a, b)
  })[0]
}

interface BestPicksRowProps {
  section: BestPicksSection
  favorites: UseFavoritesReturn
  userLocation?: UserLocation | null
}

export function BestPicksRow({ section, favorites, userLocation }: BestPicksRowProps) {
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLDivElement>(null)

  function handleVenueClick(venue: Venue) {
    Analytics.venueCardClicked(venue.id, venue.name, isVenueActiveNow(venue))
    Analytics.bestPicksSectionViewed(section.id)
    navigate(`/venue/${venue.id}`)
  }

  return (
    <div className="bp-row">
      {/* Section header */}
      <div className="bp-row-header">
        <span className="bp-icon">{section.icon}</span>
        <div>
          <h2 className="bp-title">{section.title}</h2>
          <p className="bp-subtitle">{section.subtitle}</p>
        </div>
      </div>

      {/* Compact card scroll */}
      <div className="bp-scroll" ref={scrollRef}>
        {section.venues.map(venue => {
          // EFFECTIVE status — gated on business hours. The raw version showed
          // 'Live Now' on Trending cards at 9 AM for bars that open at 11.
          const status = getEffectiveVenueStatus(venue)
          const vis = STATUS_VISUALS[status.status]
          const isOpen = isVenueActiveNow(venue)
          const topDeal = getBestDealForSection(venue, section.id)
          const isFav = favorites.isFavorite(venue.id)
          const distLabel = userLocation && venue.latitude && venue.longitude
            ? fmtDistance(distanceMiles(userLocation.lat, userLocation.lng, venue.latitude, venue.longitude))
            : null

          return (
            <div
              key={venue.id}
              className="bp-card"
              onClick={() => handleVenueClick(venue)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && handleVenueClick(venue)}
            >
              {/* Top row: status badge + heart */}
              {/* Only show badge for meaningful statuses — not 'ended' or 'not_today' */}
              <div className="bp-card-top">
                {(status.status === 'live_now' || status.status === 'ends_soon' || status.status === 'starts_soon' || status.status === 'later_today') ? (
                  <span
                    className="bp-status"
                    style={{ background: vis.bg, color: vis.text }}
                  >
                    {(isOpen || status.status === 'ends_soon') && (
                      <span
                        className={`bp-dot${vis.pulse ? ' pulse' : ''}`}
                        style={{ background: vis.dot }}
                      />
                    )}
                    {status.badge}
                  </span>
                ) : (
                  <span />
                )}
                <button
                  className={`bp-heart${isFav ? ' saved' : ''}`}
                  onClick={e => {
                    e.stopPropagation()
                    favorites.toggleFavorite(venue.id, venue.name)
                  }}
                  aria-label={isFav ? 'Unsave' : 'Save'}
                >
                  {isFav ? '♥' : '♡'}
                </button>
              </div>

              {/* Venue name */}
              <div className="bp-name">{venue.name}</div>

              {/* Neighborhood + distance */}
              <div className="bp-meta">
                {venue.neighborhood}
                {distLabel && <span className="bp-meta-dist"> · {distLabel}</span>}
              </div>

              {/* Single deal pill */}
              {topDeal ? (
                <div
                  className="bp-deal"
                  style={{
                    background: DEAL_TYPE_COLORS[topDeal.type as keyof typeof DEAL_TYPE_COLORS]?.bg,
                    color:      DEAL_TYPE_COLORS[topDeal.type as keyof typeof DEAL_TYPE_COLORS]?.text,
                  }}
                >
                  <span className="bp-deal-type">
                    {DEAL_TYPE_LABELS[topDeal.type as keyof typeof DEAL_TYPE_LABELS]}
                  </span>
                  <span className="bp-deal-desc">{topDeal.description}</span>
                  {(() => {
                    const label = fmtDealValue(topDeal as any)
                    return label ? <span className="bp-deal-price">{label}</span> : null
                  })()}
                </div>
              ) : (
                <div className="bp-no-deal">Tap to see deals →</div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
